import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError, API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { fmtKwp } from "../lib/format";
import {
  LayoutGrid, List as ListIcon, Search, Loader2, Zap, Download, FileText, ArrowDownWideNarrow,
  FilePlus2, Calendar, CalendarClock, CalendarRange, CalendarX2, X, PoundSterling, DatabaseBackup,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { PmBackupPanel } from "./pm/PmBackupPanel";
import AmFilterChip from "./pm/AmFilterChip";
import AssetManagerRollup from "./pm/AssetManagerRollup";
import { AdvanceModal, AssigneesModal } from "./pm/Modals";
import TrendHistoryModal from "./pm/TrendHistoryModal";
import { PipelineSummary, KanbanBoard, ListView } from "./pm/BoardViews";
import PipelineByYear from "./pm/PipelineByYear";
import BusinessClassToggle from "./pm/BusinessClassToggle";
import NotProceedingReasonDialog from "../components/NotProceedingReasonDialog";
import HandoverDateDialog from "../components/HandoverDateDialog";
import FilteredExportButton from "../components/FilteredExportButton";
import { CLASS_KEYS, CLASS_BY_KEY, resolveClass } from "../lib/classification";

/**
 * Project Management dashboard — Kanban + List views of the 10-stage
 * lifecycle. Advances are role-gated on the backend; the UI hides the
 * "advance" button when the current user isn't the approver.
 */
export default function ProjectManagement() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);   // {statuses, projects_by_status, role_keys, stage_approvers}
  const [users, setUsers] = useState([]);   // for assignee dropdowns
  const [view, setView] = useState(() => localStorage.getItem("pm_view") || "kanban");
  const [q, setQ] = useState("");
  const [amFilter, setAmFilter] = useState(""); // asset_manager_id or "" for all
  const [sortMode, setSortMode] = useState(() => localStorage.getItem("pm_sort") || "default"); // 'default'|'kwp'
  const [yearFilter, setYearFilter] = useState(() => localStorage.getItem("pm_year") ?? String(new Date().getUTCFullYear())); // "2026" | "2026-q1" | "unscheduled" | "all"
  const [dateSource, setDateSource] = useState(() => localStorage.getItem("pm_date_source") || "installation_date");
  const [customRange, setCustomRange] = useState({ from: "", to: "" });
  const [classFilter, setClassFilter] = useState(() => localStorage.getItem("pm_class") || "combined"); // 'combined' | 'retro' | 'development' | 'customer'
  const [lifecycleFilter, setLifecycleFilter] = useState(() => localStorage.getItem("pm_lifecycle") || ""); // '' | 'live' | 'inflight'
  // Risk (RAG) filter — multi-select Set of "red"|"amber"|"green". Empty
  // set = "no filter, show everything". Chips wired to the summary strip
  // so ops can click a card and zero-in on at-risk projects in place.
  const [riskFilter, setRiskFilter] = useState(() => {
    try {
      const raw = localStorage.getItem("pm_risk") || "";
      return new Set(raw.split(",").map(s => s.trim()).filter(x => ["red", "amber", "green"].includes(x)));
    } catch { return new Set(); }
  });
  const toggleRiskFilter = (key) => {
    setRiskFilter(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      try { localStorage.setItem("pm_risk", [...next].join(",")); } catch { /* private mode */ }
      return next;
    });
  };
  // Bulk-select state — admin-only. Set of project ids currently
  // selected. Shift-click within a column extends the selection using
  // `lastSelectedByColumn` as the anchor.
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [lastSelected, setLastSelected] = useState({ column: null, index: -1 });
  const [bulkBusy, setBulkBusy] = useState(false);
  const [transitioning, setTransitioning] = useState(null); // project awaiting advance modal
  const [assigning, setAssigning] = useState(null);          // project awaiting assignees modal
  const [dragging, setDragging] = useState(null);            // project being dragged
  const [trendStage, setTrendStage] = useState(null);        // stage object for the trend history modal
  const [npReasonRequest, setNpReasonRequest] = useState(null); // { project, targetStageKey }
  const [handoverRequest, setHandoverRequest] = useState(null); // { project, targetStageKey }

  const load = async () => {
    setLoading(true);
    try {
      const [d, us] = await Promise.all([
        api.get("/pm/dashboard"),
        api.get("/users").catch(() => ({ data: [] })),
      ]);
      setData(d.data);
      setUsers(us.data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load PM dashboard");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => { localStorage.setItem("pm_view", view); }, [view]);
  useEffect(() => { localStorage.setItem("pm_year", yearFilter); }, [yearFilter]);
  useEffect(() => { localStorage.setItem("pm_date_source", dateSource); }, [dateSource]);

  const statusMeta = useMemo(() => {
    if (!data) return {};
    const m = {};
    for (let i = 0; i < data.statuses.length; i++) {
      const s = data.statuses[i];
      m[s.key] = { ...s, index: i, next: data.statuses[i + 1]?.key || null };
    }
    return m;
  }, [data]);

  // Set of stage keys that count as "live" / operational (final_handover,
  // complete, closed, cancelled). Drives the live-vs-in-flight badge and
  // the lifecycleFilter (`live` | `inflight`).
  const liveKeys = useMemo(() => new Set(
    (data?.statuses || [])
      .filter(s => /handover|complete|closed|cancelled/i.test(s.key) || /handover|complete/i.test(s.label || ""))
      .map(s => s.key)
  ), [data]);

  // Effective "board year" date for a project: completed/operational sites
  // bucket by their completion (handover) date; in-flight sites by the chosen
  // planning date source. Keeps the live pipeline current while historical
  // sites sit under the year they were actually completed.
  const boardIso = useCallback((p) => {
    if (liveKeys.has(p.lifecycle_status)) {
      return (p.handover_date || p.installation_date || "").trim();
    }
    return (p[dateSource] || "").trim();
  }, [liveKeys, dateSource]);

  // Filter across every column by the search box + AM chip.
  const preRiskFilterByStatus = useMemo(() => {
    if (!data) return {};
    const out = {};
    const needle = q.trim().toLowerCase();
    const amKey = amFilter;
    for (const [k, list] of Object.entries(data.projects_by_status || {})) {
      // Lifecycle filter — collapse the whole column when it doesn't
      // match the "live"/"in-flight" pill the user clicked.
      if (lifecycleFilter === "live" && !liveKeys.has(k)) { out[k] = []; continue; }
      if (lifecycleFilter === "inflight" && liveKeys.has(k)) { out[k] = []; continue; }
      let filtered = list;
      if (needle) {
        filtered = filtered.filter(p =>
          (p.name || "").toLowerCase().includes(needle) ||
          (p.customer_name || "").toLowerCase().includes(needle) ||
          (p.address || "").toLowerCase().includes(needle) ||
          (p.postcode || "").toLowerCase().includes(needle),
        );
      }
      if (amKey) {
        filtered = filtered.filter(p =>
          amKey === "__unassigned__"
            ? !p.asset_manager_id
            : p.asset_manager_id === amKey,
        );
      }
      // Business classification filter — Retro / Development / Combined.
      // Splits the pipeline into two P&L books; "combined" is the default
      // portfolio view.
      if (classFilter !== "combined") {
        filtered = filtered.filter(p => resolveClass(p.business_classification) === classFilter);
      }
      // Year-bucket filter — driven by the tiles at the top of the board.
      // Accepts `YYYY` (whole year), `YYYY-qN` (quarter within a year),
      // `later` (any year that isn't this or next), or `unscheduled`
      // (no dates set on the chosen date source).
      if (yearFilter && yearFilter !== "all") {
        const now = new Date();
        const thisYr = now.getUTCFullYear();
        const nextYr = thisYr + 1;
        filtered = filtered.filter(p => {
          const iso = boardIso(p);
          if (!iso) return yearFilter === "unscheduled";
          const yr = Number(iso.slice(0, 4));
          const mo = Number(iso.slice(5, 7));
          if (yearFilter === "unscheduled") return false;
          if (yearFilter === "later") return yr !== thisYr && yr !== nextYr;
          if (yearFilter.includes("-q")) {
            const [ys, qs] = yearFilter.split("-q");
            const targetQ = Number(qs);
            const qOfProject = Math.ceil(mo / 3);
            return yr === Number(ys) && qOfProject === targetQ;
          }
          return String(yr) === yearFilter;
        });
      }
      // Custom date-range filter — from/to inclusive on the chosen date
      // source. Runs alongside the year chip (both must match if both
      // set) so ops can drill "later + within 90 days" style windows.
      if (customRange.from || customRange.to) {
        filtered = filtered.filter(p => {
          const iso = boardIso(p);
          if (!iso) return false; // unscheduled never matches a date range
          const dd = iso.slice(0, 10);
          if (customRange.from && dd < customRange.from) return false;
          if (customRange.to && dd > customRange.to) return false;
          return true;
        });
      }
      if (sortMode === "kwp") {
        // Biggest first — sort a *copy* so we don't mutate the backend payload.
        filtered = [...filtered].sort(
          (a, b) => (Number(b.system_size_kwp) || 0) - (Number(a.system_size_kwp) || 0),
        );
      }
      out[k] = filtered;
    }
    return out;
  }, [data, q, amFilter, sortMode, yearFilter, dateSource, customRange, classFilter, lifecycleFilter, liveKeys]);

  // Apply the risk-chip filter as a separate second pass so the summary
  // strip counts (which use preRiskFilterByStatus) don't self-erase when
  // the user clicks a chip.
  const filteredByStatus = useMemo(() => {
    if (riskFilter.size === 0) return preRiskFilterByStatus;
    const out = {};
    for (const [k, list] of Object.entries(preRiskFilterByStatus)) {
      out[k] = list.filter(p => riskFilter.has((p.risk_status || "green").toLowerCase()));
    }
    return out;
  }, [preRiskFilterByStatus, riskFilter]);

  // Per-column totals recomputed against the filtered view so the pipeline
  // summary always reflects what the user is actually looking at.
  const filteredTotals = useMemo(() => {
    const kwp = {};
    for (const [k, list] of Object.entries(filteredByStatus)) {
      kwp[k] = list.reduce((a, p) => a + (Number(p.system_size_kwp) || 0), 0);
    }
    return kwp;
  }, [filteredByStatus]);

  // Flat list of every visible project id after filters — feeds the
  // "Export filtered view" button (PDF/XLSX/CSV).
  const filteredIds = useMemo(() => {
    const out = [];
    for (const list of Object.values(filteredByStatus)) {
      for (const p of list) out.push(p.id);
    }
    return out;
  }, [filteredByStatus]);

  // Portfolio CAPEX rollup — same priority chain as the backend and every
  // other CAPEX surface in the app (direct £ override → per-kWp × kWp →
  // 850 £/kWp default). Filtered by whatever the user has narrowed to,
  // so the chip alongside "Portfolio capacity" reads live spend, not the
  // whole book.
  const filteredCapex = useMemo(() => {
    let sum = 0;
    for (const list of Object.values(filteredByStatus)) {
      for (const p of list) {
        const direct = p?.capex_gbp;
        if (direct != null && Number(direct) > 0) { sum += Number(direct); continue; }
        const kwp = Number(p?.system_size_kwp || 0);
        if (!kwp) continue;
        const perKwp = Number(p?.capex_per_kwp_gbp ?? 850);
        sum += kwp * perKwp;
      }
    }
    return sum;
  }, [filteredByStatus]);

  // Risk (RAG) totals across the currently-filtered board — drives the
  // summary strip at the top so ops can see 'how much of my pipeline is
  // amber' without opening the Investor Dashboard. Not-Proceeding
  // projects contribute count but zero kWp, mirroring the rest of the app.
  // Computed BEFORE the risk-chip filter so numbers don't self-erase.
  const riskTotals = useMemo(() => {
    const npStages = new Set(data?.not_proceeding_stages || []);
    const buckets = { red: { count: 0, kwp: 0 }, amber: { count: 0, kwp: 0 }, green: { count: 0, kwp: 0 } };
    for (const [, list] of Object.entries(preRiskFilterByStatus)) {
      for (const p of list) {
        const key = (p.risk_status || "green").toLowerCase();
        if (!buckets[key]) continue;
        buckets[key].count++;
        buckets[key].kwp += npStages.has(p.lifecycle_status) ? 0 : (Number(p.system_size_kwp) || 0);
      }
    }
    return buckets;
  }, [preRiskFilterByStatus, data]);

  // Recompute the Asset Manager rollup from the filtered project set so it
  // respects class/AM/search/year filters. Includes terminal stages so
  // "Marcus owns 2 dev sites" matches the Kanban card count.
  const filteredByAssetManager = useMemo(() => {
    const rollup = new Map();  // amId ("" = unassigned) → { …am, project_count, total_kwp }
    for (const [, list] of Object.entries(filteredByStatus)) {
      for (const p of list) {
        const amId = p.asset_manager_id || "";
        const bucket = rollup.get(amId) || {
          asset_manager_id: p.asset_manager_id || null,
          asset_manager_name: p.asset_manager_name || null,
          email: p.asset_manager_email || null,
          project_count: 0,
          total_kwp: 0,
        };
        bucket.project_count += 1;
        bucket.total_kwp += Number(p.system_size_kwp) || 0;
        rollup.set(amId, bucket);
      }
    }
    // Assigned AMs first, unassigned last — mirrors the backend ordering.
    const rows = Array.from(rollup.values()).sort((a, b) => {
      if (!!a.asset_manager_id !== !!b.asset_manager_id) return a.asset_manager_id ? -1 : 1;
      return (a.asset_manager_name || "").localeCompare(b.asset_manager_name || "");
    });
    return rows;
  }, [filteredByStatus]);

  // Asset-Manager options for the header filter chip. Same rollup shape
  // as above but ignores `amFilter` — otherwise the dropdown collapses to
  // the currently-selected AM and users can't switch. Respects the class
  // filter so "AM: All (3)" always matches the rollup's AM count.
  const amOptionsForChip = useMemo(() => {
    if (!data) return [];
    const rollup = new Map();
    const needle = q.trim().toLowerCase();
    for (const [, list] of Object.entries(data.projects_by_status || {})) {
      for (const p of list) {
        if (classFilter !== "combined" && resolveClass(p.business_classification) !== classFilter) continue;
        if (needle) {
          const hit =
            (p.name || "").toLowerCase().includes(needle) ||
            (p.customer_name || "").toLowerCase().includes(needle) ||
            (p.address || "").toLowerCase().includes(needle) ||
            (p.postcode || "").toLowerCase().includes(needle);
          if (!hit) continue;
        }
        const amId = p.asset_manager_id || "";
        const bucket = rollup.get(amId) || {
          asset_manager_id: p.asset_manager_id || null,
          asset_manager_name: p.asset_manager_name || null,
          project_count: 0,
        };
        bucket.project_count += 1;
        rollup.set(amId, bucket);
      }
    }
    return Array.from(rollup.values()).sort((a, b) => {
      if (!!a.asset_manager_id !== !!b.asset_manager_id) return a.asset_manager_id ? -1 : 1;
      return (a.asset_manager_name || "").localeCompare(b.asset_manager_name || "");
    });
  }, [data, classFilter, q]);

  // If the selected AM has no projects in the current class, drop the
  // amFilter so the board doesn't go blank confusingly. Runs whenever the
  // eligible AM options change (class flip, search change, dashboard reload).
  React.useEffect(() => {
    if (!amFilter) return;
    const stillEligible = amOptionsForChip.some(
      (o) => (o.asset_manager_id || "__unassigned__") === amFilter,
    );
    if (!stillEligible) setAmFilter("");
  }, [amOptionsForChip, amFilter]);


  // Live vs in-flight split — used by the tiny badge under the Portfolio
  // Capacity KPI so ops still see the operational-vs-pipeline breakdown
  // at a glance. Computed BEFORE the lifecycle filter is applied
  // (respecting only class + AM + search + year) so the pill counts stay
  // stable when clicked — otherwise clicking "2 live" would immediately
  // read "2 live · 0 in-flight" and the toggle would feel broken.
  const liveSplit = useMemo(() => {
    if (!data) return { live: 0, liveKwp: 0, inflight: 0, inflightKwp: 0 };
    const needle = q.trim().toLowerCase();
    const amKey = amFilter;
    let live = 0, liveKwp = 0, inflight = 0, inflightKwp = 0;
    for (const [k, list] of Object.entries(data.projects_by_status || {})) {
      const isLive = liveKeys.has(k);
      for (const p of list) {
        if (classFilter !== "combined" && resolveClass(p.business_classification) !== classFilter) continue;
        if (needle) {
          const hit =
            (p.name || "").toLowerCase().includes(needle) ||
            (p.customer_name || "").toLowerCase().includes(needle) ||
            (p.address || "").toLowerCase().includes(needle) ||
            (p.postcode || "").toLowerCase().includes(needle);
          if (!hit) continue;
        }
        if (amKey) {
          const match = amKey === "__unassigned__" ? !p.asset_manager_id : p.asset_manager_id === amKey;
          if (!match) continue;
        }
        const kwp = Number(p.system_size_kwp) || 0;
        if (isLive) { live += 1; liveKwp += kwp; }
        else { inflight += 1; inflightKwp += kwp; }
      }
    }
    return { live, liveKwp, inflight, inflightKwp };
  }, [data, classFilter, q, amFilter, liveKeys]);

  const totalCount = useMemo(() => {
    let n = 0;
    for (const [, list] of Object.entries(filteredByStatus)) {
      n += list.length;
    }
    return n;
  }, [filteredByStatus]);

  const totalKwp = useMemo(() => {
    let s = 0;
    for (const [, v] of Object.entries(filteredTotals)) {
      s += v;
    }
    return s;
  }, [filteredTotals]);

  // Pipeline by planned install year. Uses installation_date (planned or
  // actual — whichever is set) and falls back to handover_date so projects
  // late in the lifecycle don't drop off. Projects with neither land in
  // the "Unscheduled" bucket so admins know they still need a plan.
  //
  // Buckets are derived from the raw dataset (respecting search + AM +
  // class filters but IGNORING the year filter itself) so users can still
  // see every year's total while a year chip is active. Applying the
  // classFilter keeps the year tiles in lockstep with the Pipeline
  // Capacity KPI — otherwise switching to "Development" would still show
  // Retro/Customer kWp in each tile.
  const kwpByYear = useMemo(() => {
    const thisYear = new Date().getUTCFullYear();
    const nextYear = thisYear + 1;
    const mkBucket = () => ({
      kwp: 0, count: 0,
      ...Object.fromEntries(CLASS_KEYS.map(k => [k, 0])),
    });
    const mkQuarters = () => ({ q1: mkBucket(), q2: mkBucket(), q3: mkBucket(), q4: mkBucket() });
    // Dynamic per-year buckets — every year present in the data gets its own
    // tile (this/next always present), plus an "unscheduled" (TBC) bucket.
    const buckets = { unscheduled: mkBucket() };
    const quartersByYear = {};
    const ensure = (y) => { if (!buckets[y]) buckets[y] = mkBucket(); };
    const ensureQ = (y) => { if (!quartersByYear[y]) quartersByYear[y] = mkQuarters(); };
    ensure(thisYear); ensure(nextYear);
    if (!data) return { thisYear, nextYear, buckets, quartersByYear, yearList: [thisYear, nextYear] };

    const needle = q.trim().toLowerCase();
    for (const [stage, list] of Object.entries(data.projects_by_status || {})) {
      if (lifecycleFilter === "live" && !liveKeys.has(stage)) continue;
      if (lifecycleFilter === "inflight" && liveKeys.has(stage)) continue;
      for (const p of list) {
        if (needle) {
          const hit =
            (p.name || "").toLowerCase().includes(needle) ||
            (p.customer_name || "").toLowerCase().includes(needle) ||
            (p.address || "").toLowerCase().includes(needle) ||
            (p.postcode || "").toLowerCase().includes(needle);
          if (!hit) continue;
        }
        if (amFilter) {
          const match = amFilter === "__unassigned__" ? !p.asset_manager_id : p.asset_manager_id === amFilter;
          if (!match) continue;
        }
        const cls = resolveClass(p.business_classification);
        if (classFilter !== "combined" && cls !== classFilter) continue;
        const kwp = Number(p.system_size_kwp) || 0;
        const iso = boardIso(p);
        const yr = iso ? Number(iso.slice(0, 4)) : null;
        const key = (!yr || Number.isNaN(yr)) ? "unscheduled" : yr;
        if (key !== "unscheduled") ensure(key);
        buckets[key].kwp += kwp;
        buckets[key].count += 1;
        buckets[key][cls] += kwp;
        if (key !== "unscheduled" && iso.length >= 7) {
          const mo = Number(iso.slice(5, 7));
          const qKey = `q${Math.ceil(mo / 3)}`;
          ensureQ(yr);
          if (quartersByYear[yr][qKey]) {
            quartersByYear[yr][qKey].kwp += kwp;
            quartersByYear[yr][qKey].count += 1;
            quartersByYear[yr][qKey][cls] += kwp;
          }
        }
      }
    }
    const yearList = Object.keys(buckets)
      .filter(k => k !== "unscheduled")
      .map(Number)
      .sort((a, b) => b - a); // newest first
    return { thisYear, nextYear, buckets, quartersByYear, yearList };
  }, [data, q, amFilter, classFilter, lifecycleFilter, liveKeys, boardIso]);

  const isGlobalAdmin = user && (user.role === "admin" || user.role === "manager");

  // Active-filter combo chip data. Each entry has a { key, label, clear }
  // so the row can render a shared X-close button per filter and a
  // "Clear all" that dispatches every clear() sequentially.
  const activeFilters = useMemo(() => {
    const out = [];
    if (classFilter && classFilter !== "combined") {
      const meta = CLASS_BY_KEY[classFilter];
      out.push({
        key: "class",
        label: meta ? meta.label : classFilter,
        tone: meta?.tone,
        clear: () => { setClassFilter("combined"); localStorage.setItem("pm_class", "combined"); },
      });
    }
    if (lifecycleFilter) {
      out.push({
        key: "lifecycle",
        label: lifecycleFilter === "live" ? "Live" : "In-flight",
        tone: lifecycleFilter === "live" ? "#22A06B" : "var(--accent-hover)",
        clear: () => { setLifecycleFilter(""); localStorage.setItem("pm_lifecycle", ""); },
      });
    }
    if (amFilter) {
      const match = amOptionsForChip.find(
        (o) => (o.asset_manager_id || "__unassigned__") === amFilter,
      );
      const name = match ? (match.asset_manager_name || "Unassigned") : (amFilter === "__unassigned__" ? "Unassigned" : "AM");
      out.push({ key: "am", label: name, clear: () => setAmFilter("") });
    }
    if (yearFilter && yearFilter !== "all") {
      const pretty =
        yearFilter === "unscheduled" ? "Unscheduled (TBC)" :
        yearFilter === "later" ? "Past + 2028+" :
        yearFilter.includes("-q") ? `Q${yearFilter.split("-q")[1]} ${yearFilter.split("-q")[0]}` :
        yearFilter;
      out.push({ key: "year", label: pretty, clear: () => setYearFilter("all") });
    }
    if (customRange.from || customRange.to) {
      const from = customRange.from || "…";
      const to = customRange.to || "…";
      out.push({
        key: "range",
        label: `${from} → ${to}`,
        clear: () => setCustomRange({ from: "", to: "" }),
      });
    }
    if (q.trim()) {
      out.push({ key: "q", label: `"${q.trim()}"`, clear: () => setQ("") });
    }
    return out;
  }, [classFilter, lifecycleFilter, amFilter, amOptionsForChip, yearFilter, customRange, q]);

  const canAdvance = (project) => {
    if (isGlobalAdmin) return true;
    const roleKey = data?.stage_approvers?.[project.lifecycle_status || "solar_request"];
    if (!roleKey) return false;
    return (project.pm_assignees || {})[roleKey] === user?.id;
  };

  const canAssign = (project) => isGlobalAdmin || project.created_by === user?.id;

  // Drag-and-drop: when a card is released over a column that isn't the
  // project's current stage, we call the same advance/revert endpoint the
  // modal uses. Role gate still applies — a 403 surfaces via toast and the
  // board reloads (visual snap-back).
  const handleDropOnStage = async (project, targetStageKey) => {
    if (!project || !targetStageKey) return;
    const curStage = project.lifecycle_status || "solar_request";
    if (targetStageKey === curStage) return;
    if (!canAdvance(project)) {
      toast.error("You aren't the assigned approver for the current stage.");
      return;
    }
    // Moving to Not Proceeding — capture an optional reason via a proper
    // modal (works in mobile/webviews unlike window.prompt).
    if (targetStageKey === "not_proceeding") {
      setNpReasonRequest({ project, targetStageKey });
      return;
    }
    // Moving to Final Handover — REQUIRE a handover date so the year-bucket
    // rollup, planned-vs-actual, and analytics stay clean. Prompt-on-move
    // is easier than blocking the drop with a toast.
    if (targetStageKey === "final_handover") {
      setHandoverRequest({ project, targetStageKey });
      return;
    }
    await _moveProject(project, targetStageKey, null);
  };

  const _moveProject = async (project, targetStageKey, note, extra) => {
    try {
      const { data: res } = await api.patch(`/projects/${project.id}/lifecycle`, {
        to_stage: targetStageKey,
        ...(note != null ? { note } : {}),
        ...(extra || {}),
      });
      toast.success(`Moved to ${statusMeta[res.lifecycle_status]?.label || res.lifecycle_status}. Notifications sent.`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not move project");
    }
  };

  // Inline RAG edit from Kanban card. Optimistic-updates the local
  // dashboard payload so the badge flips instantly, then persists via
  // PATCH /projects/{pid}. On error we reload from source of truth.
  const handleRiskChange = async (project, nextKey) => {
    const prevKey = project.risk_status;
    setData(d => {
      if (!d) return d;
      const groups = { ...d.projects_by_status };
      for (const stage of Object.keys(groups)) {
        groups[stage] = groups[stage].map(p =>
          p.id === project.id ? { ...p, risk_status: nextKey } : p
        );
      }
      return { ...d, projects_by_status: groups };
    });
    try {
      await api.patch(`/projects/${project.id}`, { risk_status: nextKey });
      toast.success(`Risk set to ${nextKey.charAt(0).toUpperCase() + nextKey.slice(1)}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not update risk");
      // Roll back optimistic update
      setData(d => {
        if (!d) return d;
        const groups = { ...d.projects_by_status };
        for (const stage of Object.keys(groups)) {
          groups[stage] = groups[stage].map(p =>
            p.id === project.id ? { ...p, risk_status: prevKey } : p
          );
        }
        return { ...d, projects_by_status: groups };
      });
    }
  };

  // Toggle bulk-selection for a single card. Shift-click extends the
  // selection from `lastSelected` to the clicked card WITHIN THE SAME
  // COLUMN (Kanban cards are naturally column-scoped so extending across
  // columns rarely makes sense in a triage flow).
  const toggleSelected = (project, columnKey, indexInColumn, shiftKey) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (shiftKey && lastSelected.column === columnKey && lastSelected.index >= 0) {
        const list = filteredByStatus[columnKey] || [];
        const [a, b] = [lastSelected.index, indexInColumn].sort((x, y) => x - y);
        for (let i = a; i <= b && i < list.length; i++) {
          next.add(list[i].id);
        }
      } else {
        if (next.has(project.id)) next.delete(project.id);
        else next.add(project.id);
      }
      return next;
    });
    setLastSelected({ column: columnKey, index: indexInColumn });
  };
  const clearSelection = () => { setSelectedIds(new Set()); setLastSelected({ column: null, index: -1 }); };

  // Marquee (click-and-drag rectangle) multi-select — the MarqueeSelector
  // returns ids covered by the drawn rectangle and whether Shift was held.
  // Shift = additive to the current selection; no Shift = replace.
  const handleMarqueeSelect = (ids, { shiftKey } = {}) => {
    if (!ids || ids.length === 0) {
      if (!shiftKey) clearSelection();
      return;
    }
    setSelectedIds(prev => {
      if (shiftKey) {
        const next = new Set(prev);
        for (const id of ids) next.add(id);
        return next;
      }
      return new Set(ids);
    });
    setLastSelected({ column: null, index: -1 }); // marquee doesn't set a column anchor
  };

  // Bulk-flip risk for every selected project. Fires one PATCH per
  // project in parallel (keeps optimistic-UI simple + preserves server-
  // side audit trail per project). Success/failure toast summarises.
  const handleBulkRisk = async (nextKey) => {
    if (selectedIds.size === 0) return;
    const ids = [...selectedIds];
    setBulkBusy(true);
    // Optimistic update
    setData(d => {
      if (!d) return d;
      const groups = { ...d.projects_by_status };
      for (const stage of Object.keys(groups)) {
        groups[stage] = groups[stage].map(p =>
          ids.includes(p.id) ? { ...p, risk_status: nextKey } : p
        );
      }
      return { ...d, projects_by_status: groups };
    });
    const results = await Promise.allSettled(
      ids.map(id => api.patch(`/projects/${id}`, { risk_status: nextKey }))
    );
    const failed = results.filter(r => r.status === "rejected").length;
    setBulkBusy(false);
    if (failed === 0) {
      toast.success(`${ids.length} project${ids.length === 1 ? "" : "s"} set to ${nextKey.charAt(0).toUpperCase() + nextKey.slice(1)}`);
      clearSelection();
    } else {
      toast.error(`${failed} of ${ids.length} update${ids.length === 1 ? "" : "s"} failed — reloading`);
      clearSelection();  // clear stale selection so the ghost toolbar dismisses too
      load();
    }
  };

  return (
    <div className="p-6 lg:p-10 max-w-[1600px] mx-auto" data-testid="pm-page">
      <div className="flex items-end justify-between mb-8 gap-6 flex-wrap">
        <div>
          <div className="label mb-2">Project Management</div>
          <h1 className="font-display text-4xl font-bold">Lifecycle Board</h1>
          <p className="muted text-sm mt-2">
            Every solar project from Solar Request through Final Handover, gated by role and audited on every step.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/projects/new"
            className="btn-accent text-xs"
            data-testid="pm-new-project-btn"
            title="Create a project manually, or import from SolarEdge/PV*SOL/PDF — starts in the first lifecycle stage"
          >
            <FilePlus2 size={13} /> New / Import
          </Link>
          <FilteredExportButton
            ids={filteredIds}
            title="Lifecycle board export"
            testIdPrefix="pm-export"
            layoutScope="pm-board"
          />
          {user?.role === "admin" && (
            <Dialog>
              <DialogTrigger asChild>
                <button className="btn-ghost text-xs" data-testid="pm-backup-btn" title="Back up or restore the Lifecycle Board">
                  <DatabaseBackup size={13} /> Backup
                </button>
              </DialogTrigger>
              <DialogContent className="max-w-xl" data-testid="pm-backup-dialog">
                <DialogHeader><DialogTitle>Lifecycle Board backup</DialogTitle></DialogHeader>
                <PmBackupPanel onRestored={load} />
              </DialogContent>
            </Dialog>
          )}
          <div className="inline-flex rounded border border-[var(--line)] overflow-hidden ml-2" data-testid="pm-view-toggle">
            <button
              onClick={() => setView("kanban")}
              className={`px-3 py-2 text-xs inline-flex items-center gap-1.5 ${view === "kanban" ? "bg-[var(--ink)] text-white" : "bg-white"}`}
              data-testid="pm-view-kanban"
            >
              <LayoutGrid size={13} /> Kanban
            </button>
            <button
              onClick={() => setView("list")}
              className={`px-3 py-2 text-xs inline-flex items-center gap-1.5 ${view === "list" ? "bg-[var(--ink)] text-white" : "bg-white"}`}
              data-testid="pm-view-list"
            >
              <ListIcon size={13} /> List
            </button>
          </div>
          <button
            onClick={() => {
              const next = sortMode === "kwp" ? "default" : "kwp";
              setSortMode(next);
              localStorage.setItem("pm_sort", next);
            }}
            className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs rounded border ml-2 transition-colors ${
              sortMode === "kwp"
                ? "bg-[var(--accent)] text-white border-[var(--accent)]"
                : "bg-white border-[var(--line)] hover:border-[var(--accent)]"
            }`}
            data-testid="pm-sort-toggle"
            title={
              sortMode === "kwp"
                ? "Currently: biggest kWp first — click to restore default order"
                : "Click to sort every column by kWp (biggest first)"
            }
          >
            <ArrowDownWideNarrow size={13} />
            {sortMode === "kwp" ? "Sort: kWp ↓" : "Sort: kWp"}
          </button>
        </div>
      </div>

      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <div className="flex items-center gap-2 input-box max-w-sm flex-1 min-w-[240px]">
          <Search size={14} className="muted" />
          <input
            data-testid="pm-search-input"
            className="flex-1 outline-none bg-transparent text-sm"
            placeholder="Search projects…"
            value={q} onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <AmFilterChip
          byAssetManager={amOptionsForChip}
          value={amFilter}
          onChange={setAmFilter}
        />
        <div className="inline-flex items-center gap-4 ml-auto">
          <div className="text-right" data-testid="pm-summary-projects">
            <div className="label !text-[9px]">Projects</div>
            <div className="font-display text-2xl font-bold leading-tight">{totalCount}</div>
          </div>
          <div className="h-8 w-px bg-[var(--line)]" />
          <div className="text-right" data-testid="pm-summary-capex" title="Total resolved CAPEX across the currently-filtered view">
            <div className="label !text-[9px]">Portfolio CAPEX</div>
            <div className="font-display text-2xl font-bold leading-tight flex items-center gap-1.5 justify-end">
              <PoundSterling size={18} />
              {new Intl.NumberFormat("en-GB", { notation: filteredCapex >= 1_000_000 ? "compact" : "standard", maximumFractionDigits: filteredCapex >= 1_000_000 ? 2 : 0 }).format(filteredCapex)}
            </div>
            <div className="text-[10px] muted mt-0.5">
              across {filteredIds.length} project{filteredIds.length === 1 ? "" : "s"}
            </div>
          </div>
          <div className="h-8 w-px bg-[var(--line)]" />
          <div className="text-right" data-testid="pm-summary-kwp">
            <div className="label !text-[9px]">Portfolio capacity</div>
            <div className="font-display text-2xl font-bold leading-tight flex items-center gap-1.5 justify-end" style={{ color: "var(--accent-hover)" }}>
              <Zap size={18} />
              {fmtKwp(totalKwp)}
            </div>            {(liveSplit.live > 0 || liveSplit.inflight > 0) && (
              <div
                className="mt-0.5 text-[10px] font-mono tabular-nums inline-flex items-center justify-end gap-1"
                data-testid="pm-summary-split"
              >
                <button
                  type="button"
                  onClick={() => {
                    const next = lifecycleFilter === "live" ? "" : "live";
                    setLifecycleFilter(next);
                    localStorage.setItem("pm_lifecycle", next);
                  }}
                  className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 transition-colors ${
                    lifecycleFilter === "live"
                      ? "bg-[#DBF3E4] text-[#17663C] font-semibold"
                      : "muted hover:bg-[var(--surface-2,#F6F7F9)]"
                  }`}
                  data-testid="pm-summary-split-live"
                  data-active={lifecycleFilter === "live" ? "true" : "false"}
                  title={`${fmtKwp(liveSplit.liveKwp)} operational — click to filter`}
                  aria-pressed={lifecycleFilter === "live"}
                >
                  <span
                    aria-hidden
                    className="inline-block rounded-full"
                    style={{ width: 6, height: 6, background: "#22A06B" }}
                  />
                  {liveSplit.live} live
                </button>
                <span className="opacity-40">·</span>
                <button
                  type="button"
                  onClick={() => {
                    const next = lifecycleFilter === "inflight" ? "" : "inflight";
                    setLifecycleFilter(next);
                    localStorage.setItem("pm_lifecycle", next);
                  }}
                  className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 transition-colors ${
                    lifecycleFilter === "inflight"
                      ? "bg-[#E4EDF7] text-[var(--accent-hover)] font-semibold"
                      : "muted hover:bg-[var(--surface-2,#F6F7F9)]"
                  }`}
                  data-testid="pm-summary-split-inflight"
                  data-active={lifecycleFilter === "inflight" ? "true" : "false"}
                  title={`${fmtKwp(liveSplit.inflightKwp)} still in-flight — click to filter`}
                  aria-pressed={lifecycleFilter === "inflight"}
                >
                  <span
                    aria-hidden
                    className="inline-block rounded-full"
                    style={{ width: 6, height: 6, background: "var(--accent-hover)" }}
                  />
                  {liveSplit.inflight} in-flight
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {activeFilters.length > 0 && (
        <div
          className="mb-3 flex items-center flex-wrap gap-1.5 text-xs"
          data-testid="pm-active-filters"
        >
          <span className="label !text-[9px] !text-[var(--ink-3,#999)] pr-1">Filters</span>
          {activeFilters.map((f) => (
            <span
              key={f.key}
              data-testid={`pm-filter-chip-${f.key}`}
              className="inline-flex items-center gap-1.5 rounded-full pl-2 pr-1 py-0.5 border text-[11px] font-medium bg-white"
              style={{
                borderColor: f.tone || "var(--line)",
                color: f.tone || "var(--ink)",
              }}
            >
              {f.tone && (
                <span
                  aria-hidden
                  className="inline-block rounded-full"
                  style={{ width: 6, height: 6, background: f.tone }}
                />
              )}
              {f.label}
              <button
                type="button"
                onClick={f.clear}
                className="inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-[var(--surface-2,#F6F7F9)] transition-colors"
                title={`Clear ${f.label} filter`}
                aria-label={`Clear ${f.label} filter`}
                data-testid={`pm-filter-chip-${f.key}-clear`}
              >
                <X size={10} />
              </button>
            </span>
          ))}
          {activeFilters.length > 1 && (
            <button
              type="button"
              onClick={() => activeFilters.forEach((f) => f.clear())}
              className="text-[11px] muted hover:text-[var(--ink)] underline decoration-dotted underline-offset-2 ml-1"
              data-testid="pm-filter-clear-all"
            >
              Clear all
            </button>
          )}
        </div>
      )}

      {!loading && data && (
        <BusinessClassToggle
          data={data}
          classFilter={classFilter}
          setClassFilter={(v) => { setClassFilter(v); localStorage.setItem("pm_class", v); }}
        />
      )}

      {!loading && data && (
        <PipelineByYear
          kwpByYear={kwpByYear}
          yearFilter={yearFilter}
          setYearFilter={setYearFilter}
          dateSource={dateSource}
          setDateSource={(v) => { setDateSource(v); localStorage.setItem("pm_date_source", v); }}
          customRange={customRange}
          setCustomRange={setCustomRange}
        />
      )}

      {!loading && data && (
        <PipelineSummary
          statuses={data.statuses}
          filteredTotals={filteredTotals}
          filteredByStatus={filteredByStatus}
        />
      )}

      {/* Risk (RAG) summary strip — mirrors the Investor Dashboard chips
          but sits at the top of the Lifecycle Board so ops can see risk
          posture (and click through to filter Kanban in-place) in one
          place. Each card doubles as a filter chip (multi-select). */}
      {!loading && data && (
        <div className="mb-6" data-testid="pm-risk-summary-strip">
          {riskFilter.size > 0 && (
            <div className="flex items-center justify-between mb-2">
              <div className="label !text-[10px]">
                Showing {[...riskFilter].map(r => r.toUpperCase()).join(" + ")} only
              </div>
              <button
                type="button"
                onClick={() => { setRiskFilter(new Set()); try { localStorage.removeItem("pm_risk"); } catch {} }}
                className="text-[10px] muted hover:underline"
                data-testid="pm-risk-clear"
              >
                Clear filter
              </button>
            </div>
          )}
          <div className="grid grid-cols-3 gap-2">
            {[
              { key: "red",   label: "Red",   sub: "At risk",  tone: "#C0362C" },
              { key: "amber", label: "Amber", sub: "Watch",    tone: "#B8721E" },
              { key: "green", label: "Green", sub: "On track", tone: "#0F8B84" },
            ].map(r => {
              const t = riskTotals[r.key] || { count: 0, kwp: 0 };
              const active = riskFilter.has(r.key);
              return (
                <button
                  type="button"
                  key={r.key}
                  onClick={() => toggleRiskFilter(r.key)}
                  aria-pressed={active}
                  className={`p-3 rounded-lg text-left flex items-center justify-between gap-2 border-2 transition-all ${
                    active ? "shadow-sm" : "bg-white border-[var(--line)] hover:border-[var(--accent)]"
                  }`}
                  style={active ? { borderColor: r.tone, background: `${r.tone}12` } : undefined}
                  data-testid={`pm-risk-${r.key}`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span style={{ width: 12, height: 12, background: r.tone, borderRadius: "50%", display: "inline-block", flexShrink: 0 }} />
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase tracking-[0.16em] font-semibold" style={{ color: r.tone }}>
                        {r.label}
                      </div>
                      <div className="text-[10px] muted">{r.sub}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-lg font-bold leading-tight" style={{ color: r.tone }}>
                      {t.count}
                    </div>
                    <div className="text-[10px] muted mt-0.5">{fmtKwp(t.kwp)}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {!loading && data && (
        <AssetManagerRollup
          byAssetManager={filteredByAssetManager}
          roleKeys={data.role_keys || []}
          users={users}
          canManage={isGlobalAdmin}
          onReassignDone={load}
        />
      )}

      {loading && (
        <div className="flex items-center justify-center py-16 muted text-sm gap-2" data-testid="pm-loading">
          <Loader2 size={16} className="animate-spin" /> Loading dashboard…
        </div>
      )}

      {/* Bulk RAG action toolbar — appears when at least one card is
          selected (shift-click extends within a column). Admin/manager
          only. Fires PATCH per project in parallel and keeps optimistic
          UI in sync with rollback on error. */}
      {isGlobalAdmin && selectedIds.size > 0 && (
        <div
          className="mb-4 flex items-center justify-between gap-2 p-3 rounded-lg"
          style={{ background: "var(--accent-hover)10", border: "1.5px solid var(--accent-hover)" }}
          data-testid="pm-bulk-toolbar"
        >
          <div className="flex items-center gap-2 text-sm">
            <span className="font-semibold" data-testid="pm-bulk-count">
              {selectedIds.size} selected
            </span>
            <span className="muted text-xs">·  Bulk set risk to:</span>
          </div>
          <div className="flex items-center gap-2">
            {[
              { key: "red",   label: "Red",   tone: "#C0362C" },
              { key: "amber", label: "Amber", tone: "#B8721E" },
              { key: "green", label: "Green", tone: "#0F8B84" },
            ].map(r => (
              <button
                key={r.key}
                type="button"
                onClick={() => handleBulkRisk(r.key)}
                disabled={bulkBusy}
                className="px-3 py-1.5 rounded text-xs font-semibold flex items-center gap-1.5"
                style={{ background: r.tone, color: "white", opacity: bulkBusy ? 0.6 : 1 }}
                data-testid={`pm-bulk-set-${r.key}`}
              >
                <span style={{ width: 8, height: 8, background: "white", borderRadius: "50%" }} />
                {r.label}
              </button>
            ))}
            <button
              type="button"
              onClick={clearSelection}
              className="btn-ghost text-xs"
              data-testid="pm-bulk-clear"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {!loading && data && view === "kanban" && (
        <KanbanBoard
          data={data}
          statusMeta={statusMeta}
          projectsByStatus={filteredByStatus}
          filteredTotals={filteredTotals}
          canAdvance={canAdvance}
          canAssign={canAssign}
          onAdvance={setTransitioning}
          onAssign={setAssigning}
          onRiskChange={handleRiskChange}
          canBulkSelect={isGlobalAdmin}
          selectedIds={selectedIds}
          onToggleSelected={toggleSelected}
          onMarqueeSelect={handleMarqueeSelect}
          dragging={dragging}
          setDragging={setDragging}
          onDropStage={handleDropOnStage}
          onOpenTrend={setTrendStage}
          canCreateAtStage={isGlobalAdmin}
        />
      )}

      {!loading && data && view === "list" && (
        <ListView
          data={data}
          statusMeta={statusMeta}
          projectsByStatus={filteredByStatus}
          filteredTotals={filteredTotals}
          canAdvance={canAdvance}
          canAssign={canAssign}
          onAdvance={setTransitioning}
          onAssign={setAssigning}
          onRiskChange={handleRiskChange}
        />
      )}

      {transitioning && (
        <AdvanceModal
          project={transitioning}
          data={data}
          statusMeta={statusMeta}
          onClose={() => setTransitioning(null)}
          onDone={() => { setTransitioning(null); load(); }}
        />
      )}

      {assigning && (
        <AssigneesModal
          project={assigning}
          data={data}
          users={users}
          onClose={() => setAssigning(null)}
          onDone={() => { setAssigning(null); load(); }}
        />
      )}

      {trendStage && (
        <TrendHistoryModal
          stageKey={trendStage.key}
          stageLabel={trendStage.label}
          slaDays={trendStage.sla_days}
          onClose={() => setTrendStage(null)}
        />
      )}

      {npReasonRequest && (
        <NotProceedingReasonDialog
          project={npReasonRequest.project}
          onCancel={() => setNpReasonRequest(null)}
          onConfirm={async (reason) => {
            const req = npReasonRequest;
            setNpReasonRequest(null);
            await _moveProject(req.project, req.targetStageKey, reason || null);
          }}
        />
      )}

      {handoverRequest && (
        <HandoverDateDialog
          project={handoverRequest.project}
          onCancel={() => setHandoverRequest(null)}
          onConfirm={async ({ handover_date, note }) => {
            const req = handoverRequest;
            setHandoverRequest(null);
            await _moveProject(req.project, req.targetStageKey, note || null, { handover_date });
          }}
        />
      )}
    </div>
  );
}
